import { RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useI18n } from '../../../i18n/useI18n';
import { LiksButton } from '../LiksButton/LiksButton';
import './LiksRefreshButton.css';

const LIKS_MANUAL_REFRESH_COOLDOWN_MS = 5_000;

export type LiksRefreshButtonProps = {
  onRefresh: () => void;
  className?: string;
  disabled?: boolean;
  cooldownMs?: number;
};

export function LiksRefreshButton({
  onRefresh,
  className,
  disabled = false,
  cooldownMs = LIKS_MANUAL_REFRESH_COOLDOWN_MS,
}: LiksRefreshButtonProps) {
  const { translate } = useI18n();
  const [isCoolingDown, setIsCoolingDown] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleClick = () => {
    if (disabled || isCoolingDown) {
      return;
    }

    onRefresh();
    setIsCoolingDown(true);
    timeoutRef.current = window.setTimeout(() => {
      setIsCoolingDown(false);
      timeoutRef.current = null;
    }, cooldownMs);
  };

  return (
    <LiksButton
      type="button"
      className={[
        'liks-refresh-button',
        isCoolingDown && 'liks-refresh-button--cooling',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={
        {
          '--liks-refresh-cooldown-ms': `${cooldownMs}ms`,
        } as CSSProperties
      }
      variant="secondary"
      disabled={disabled || isCoolingDown}
      onClick={handleClick}
    >
      <RefreshCw aria-hidden /> {translate('common.refresh')}
    </LiksButton>
  );
}
